import { useLingui } from '../../../../../../node_modules/@lingui/react/dist/index.mjs';
import React__default from 'react';
import Column from '../atoms/Column.mjs';
import Typography from '../atoms/Typography.mjs';
import VisuallyHidden from '../atoms/VisuallyHidden.mjs';
import modules_c0befac8 from './PasswordError.module.css.mjs';
import { PasswordStrengthCheck } from './PasswordStrengthCheck.mjs';

// This list of warnings and suggestions should be exhaustive, and is pulled from https://github.com/stytchauth/zxcvbn/blob/main/feedback/feedback.go
const warnings = {
    'Short keyboard patterns are easy to guess.': {
        id: "password.zxcvbn.warning.shortKeyboardPatterns"
    },
    'Straight rows of keys are easy to guess.': {
        id: "password.zxcvbn.warning.straightRows"
    },
    'Repeats like "aaa" are easy to guess.': {
        id: "password.zxcvbn.warning.repeats"
    },
    'Repeats like "abcabcabc" are only slightly harder to guess than "abc".': {
        id: "password.zxcvbn.warning.longerRepeats"
    },
    'Sequences like "abc" or "6543" are easy to guess.': {
        id: "password.zxcvbn.warning.sequences"
    },
    'Recent years are easy to guess.': {
        id: "password.zxcvbn.warning.recentYears"
    },
    'Dates are often easy to guess.': {
        id: "password.zxcvbn.warning.dates"
    },
    'This is a top-10 common password.': {
        id: "password.zxcvbn.warning.top10CommonPassword"
    },
    'This is a top-100 common password.': {
        id: "password.zxcvbn.warning.top100CommonPassword"
    },
    'This is a very common password.': {
        id: "password.zxcvbn.warning.veryCommonPassword"
    },
    'This is similar to a commonly used password.': {
        id: "password.zxcvbn.warning.similarToCommonPassword"
    },
    'A word by itself is easy to guess.': {
        id: "password.zxcvbn.warning.wordByItself"
    },
    'Names and surnames by themselves are easy to guess.': {
        id: "password.zxcvbn.warning.names"
    },
    'Common names and surnames are easy to guess.': {
        id: "password.zxcvbn.warning.commonNames"
    }
};
const suggestions = {
    'Use a few words, avoid common phrases.': {
        id: "password.zxcvbn.suggestion.useAFewWords"
    },
    'No need for symbols, digits, or uppercase letters.': {
        id: "password.zxcvbn.suggestion.noSymbolsDigitsUppercase"
    },
    'Add another word or two. Uncommon words are better.': {
        id: "password.zxcvbn.suggestion.addWords"
    },
    'Use a longer keyboard pattern with more turns.': {
        id: "password.zxcvbn.suggestion.longerKeyboardPattern"
    },
    'Avoid repeated words and characters.': {
        id: "password.zxcvbn.suggestion.avoidRepeats"
    },
    'Avoid sequences.': {
        id: "password.zxcvbn.suggestion.avoidSequences"
    },
    'Avoid recent years.': {
        id: "password.zxcvbn.suggestion.avoidRecentYears"
    },
    'Avoid years that are associated with you.': {
        id: "password.zxcvbn.suggestion.avoidYearsAssociatedWithYou"
    },
    'Avoid dates and years that are associated with you.': {
        id: "password.zxcvbn.suggestion.avoidDatesAndYearsAssociatedWithYou"
    },
    "Capitalization doesn't help very much.": {
        id: "password.zxcvbn.suggestion.capitalization"
    },
    'All-uppercase is almost as easy to guess as all-lowercase.': {
        id: "password.zxcvbn.suggestion.allUppercase"
    },
    "Reversed words aren't much harder to guess.": {
        id: "password.zxcvbn.suggestion.reversed"
    },
    "Predictable substitutions like '@' instead of 'a' don't help very much.": {
        id: "password.zxcvbn.suggestion.predictableSubstitutions"
    }
};
const labels = {
    fulfilled: {
        id: "password.requirement.fulfilled"
    },
    notFulfilled: {
        id: "password.requirement.notFulfilled"
    },
    warning: {
        id: "password.requirement.warning"
    },
    suggestion: {
        id: "password.requirement.suggestion"
    }
};
const CrossIcon = ()=>/*#__PURE__*/ React__default.createElement("svg", {
        viewBox: "0 0 16 16",
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg"
    }, /*#__PURE__*/ React__default.createElement("path", {
        d: "M12.6667 4.27334L11.7267 3.33334L8.00001 7.06001L4.27334 3.33334L3.33334 4.27334L7.06001 8.00001L3.33334 11.7267L4.27334 12.6667L8.00001 8.94001L11.7267 12.6667L12.6667 11.7267L8.94001 8.00001L12.6667 4.27334Z",
        fill: "currentColor"
    }));
const CheckIcon = ()=>/*#__PURE__*/ React__default.createElement("svg", {
        viewBox: "0 0 16 16",
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg"
    }, /*#__PURE__*/ React__default.createElement("path", {
        d: "M5.86332 10.5833L3.08332 7.80333L2.13666 8.74333L5.86332 12.47L13.8633 4.47L12.9233 3.53L5.86332 10.5833Z",
        fill: "currentColor"
    }));
const PasswordStrengthItem = ({ children, type })=>{
    const { i18n: $__i18n, _: $__ } = useLingui();
    // notFulfilled and warning both have the same icon and color, they only differ in label
    const color = type === 'suggestion' ? undefined //
     : type === 'fulfilled' ? 'success' : 'destructive';
    const icon = type === 'suggestion' ? /*#__PURE__*/ React__default.createElement("span", {
        role: "presentation",
        className: modules_c0befac8.bullet
    }) : type === 'fulfilled' ? /*#__PURE__*/ React__default.createElement(CheckIcon, null) : /*#__PURE__*/ React__default.createElement(CrossIcon, null);
    return /*#__PURE__*/ React__default.createElement(Typography, {
        variant: "helper",
        className: modules_c0befac8.item,
        color: color,
        "aria-atomic": true
    }, icon, /*#__PURE__*/ React__default.createElement("span", null, /*#__PURE__*/ React__default.createElement(VisuallyHidden, null, $__i18n._(labels[type]), ": "), children));
};
const PasswordError = ({ bootstrap, ...props })=>{
    const { i18n: $__i18n, _: $__ } = useLingui();
    const complexity = bootstrap.passwordConfig?.ludsComplexity ?? 0;
    const characterLength = bootstrap.passwordConfig?.ludsMinimumCount ?? 0;
    if (props.policy === 'notLoaded') {
        return null;
    }
    const { passwordInvalid, isPasswordBreached } = props;
    const passwordBreachedWarning = isPasswordBreached ? /*#__PURE__*/ React__default.createElement(PasswordStrengthItem, {
        type: "warning"
    }, $__i18n._({
        id: "password.strengthError.breached"
    })) : null;
    if (props.policy === 'zxcvbn') {
        const { passwordScore, passwordWarning, passwordSuggestions } = props;
        // The list of warnings and suggestions above should be exhaustive, but we'll fall back to the untranslated string just in case.
        const translatedWarning = passwordWarning ? warnings[passwordWarning] ?? passwordWarning : null;
        const uniqueSuggestions = [
            ...new Set(passwordSuggestions)
        ];
        const translatedSuggestions = uniqueSuggestions.map((suggestion)=>suggestions[suggestion] ?? suggestion);
        return /*#__PURE__*/ React__default.createElement(Column, {
            gap: 1,
            "aria-live": "polite"
        }, /*#__PURE__*/ React__default.createElement(PasswordStrengthCheck, {
            score: passwordScore
        }), /*#__PURE__*/ React__default.createElement(VisuallyHidden, null, passwordInvalid ? $__i18n._({
            id: "password.strength.notAccepted"
        }) : $__i18n._({
            id: "password.strength.accepted"
        })), /*#__PURE__*/ React__default.createElement("div", null, translatedWarning && /*#__PURE__*/ React__default.createElement(PasswordStrengthItem, {
            type: "warning"
        }, $__i18n._(translatedWarning)), translatedSuggestions.map((suggestion)=>/*#__PURE__*/ React__default.createElement(PasswordStrengthItem, {
                key: suggestion.id,
                type: "suggestion"
            }, $__i18n._(suggestion))), passwordBreachedWarning));
    }
    const { missingCharacters, missingComplexity } = props;
    return /*#__PURE__*/ React__default.createElement("div", {
        "aria-live": "polite"
    }, /*#__PURE__*/ React__default.createElement(VisuallyHidden, null, passwordInvalid ? $__i18n._({
        id: "password.strength.requirementsNotMet"
    }) : $__i18n._({
        id: "password.strength.accepted"
    })), /*#__PURE__*/ React__default.createElement(PasswordStrengthItem, {
        type: missingCharacters === 0 ? 'fulfilled' : 'notFulfilled'
    }, $__i18n._({
        id: "password.strengthError.minimumLength",
        values: {
            characterLength: characterLength
        }
    })), /*#__PURE__*/ React__default.createElement(PasswordStrengthItem, {
        type: missingComplexity === 0 ? 'fulfilled' : 'notFulfilled'
    }, $__i18n._({
        id: "password.strengthError.complexity",
        values: {
            complexity: complexity
        }
    })), passwordBreachedWarning);
};
function getNewPasswordProps(bootstrap, passwordPolicy) {
    if (passwordPolicy === 'notLoaded' || passwordPolicy === 'zxcvbn' || bootstrap.passwordConfig == null) {
        // passwordrules is currently only respected by password managers so this is a safe default
        // we don't set minLength to avoid blocking
        // https://developer.apple.com/password-rules/
        return {
            type: 'new',
            passwordrules: 'allowed: unicode; minlength: 10'
        };
    }
    const { ludsComplexity, ludsMinimumCount } = bootstrap.passwordConfig;
    const required = [
        'special',
        'digit',
        'upper',
        'lower'
    ].slice(0, ludsComplexity);
    return {
        type: 'new',
        minLength: ludsMinimumCount,
        passwordrules: `allowed: unicode; minlength: ${ludsMinimumCount}; ${required.map((r)=>`required: ${r}`).join('; ')}`
    };
}

export { PasswordError, getNewPasswordProps };
//# sourceMappingURL=PasswordError.mjs.map
